import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import styled from 'styled-components';
import {
  Scrim,
  Modal,
  Btn,
  Field,
  Input,
  Select,
  FormGrid,
} from '../ui/primitives';
import Toggle from '../ui/Toggle';
import {
  createActivationCode,
  updateActivationCode,
  generateActivationCode,
  fetchOrganizations,
} from '../services/activationCodeAdminApi';

const TIER_OPTIONS = [
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'growth', label: 'Growth' },
  { value: 'organization', label: 'Organization' },
  { value: 'enterprise', label: 'Enterprise' },
];

const APPLICABILITY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const EMPTY = {
  scope: 'global',     // 'global' (anyone) | 'org' (tied to one org, one-time)
  code: '',
  planId: 'professional',
  orgId: '',
  applicability: 'monthly',
  validFrom: '',
  validUntil: '',
  maxTotalUses: '',
  active: 1,
  customDuration: false, // when off, plan runs the standard monthly/yearly term
  grantValue: '',        // grant length the redeemer receives (blank = plan default)
  grantUnit: 'months',   // 'months' | 'days'
};

/** grant_duration_days (from the API) → { grantValue, grantUnit } for the form. */
function daysToGrant(days) {
  if (days === null || days === undefined || days === '') return { grantValue: '', grantUnit: 'months' };
  const n = Number(days);
  if (Number.isNaN(n) || n <= 0) return { grantValue: '', grantUnit: 'months' };
  if (n % 30 === 0) return { grantValue: String(n / 30), grantUnit: 'months' };
  return { grantValue: String(n), grantUnit: 'days' };
}

/** { grantValue, grantUnit } → grant_duration_days, or null when blank. */
function grantToDays(grantValue, grantUnit) {
  const v = String(grantValue).trim();
  if (v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n) || n <= 0) return null;
  return grantUnit === 'months' ? Math.round(n * 30) : Math.round(n);
}

/** epoch seconds → 'YYYY-MM-DD' for a date input (UTC). */
function epochToDate(epoch) {
  if (epoch === null || epoch === undefined || epoch === '') return '';
  const d = new Date(Number(epoch) * 1000);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD' → epoch seconds (UTC midnight), or null when blank. */
function dateToEpoch(dateStr) {
  if (!dateStr) return null;
  const ms = Date.parse(`${dateStr}T00:00:00Z`);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

/**
 * Create/edit dialog for an activation code. When `code` is provided the dialog edits
 * it (PUT); otherwise it creates a new one (POST). Calls `onSaved()` on success.
 */
export default function ActivationCodeFormDialog({ open, code, onClose, onSaved }) {
  const isEdit = Boolean(code?.activationCodeId);
  const [form, setForm] = useState(EMPTY);
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    if (isEdit) {
      const { grantValue, grantUnit } = daysToGrant(code.grantDurationDays);
      setForm({
        scope: code.orgId ? 'org' : 'global',
        code: code.code || '',
        planId: code.planId || 'professional',
        orgId: code.orgId || '',
        applicability: code.applicability === 'yearly' ? 'yearly' : 'monthly',
        validFrom: epochToDate(code.validFrom),
        validUntil: epochToDate(code.validUntil),
        maxTotalUses: code.maxTotalUses ?? '',
        active: code.active ? 1 : 0,
        customDuration: Boolean(code.grantDurationDays),
        grantValue,
        grantUnit,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, isEdit, code]);

  // Load the org list once the dialog opens (for the org picker). Surface load
  // state/errors so a failed fetch isn't a silently-empty dropdown.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setOrgsError('');
    setOrgsLoading(true);
    fetchOrganizations()
      .then((data) => { if (alive) setOrgs(Array.isArray(data) ? data : []); })
      .catch((e) => { if (alive) { setOrgs([]); setOrgsError(e.message || 'Could not load organizations.'); } })
      .finally(() => { if (alive) setOrgsLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const generated = await generateActivationCode();
      set('code', generated);
    } catch (e) {
      setError(e.message || 'Could not generate a code.');
    } finally {
      setGenerating(false);
    }
  };

  const isOrgScoped = form.scope === 'org';

  const handleSave = async () => {
    if (!form.code.trim()) {
      setError('Code is required.');
      return;
    }
    if (isOrgScoped && !form.orgId) {
      setError('Select an organization for an organization-specific code.');
      return;
    }
    // Organization-specific codes are one-time (single seat); global codes use the
    // admin-entered max (blank = unlimited).
    const maxUses = String(form.maxTotalUses).trim();
    const payload = {
      code: form.code.trim().toUpperCase(),
      planId: form.planId,
      orgId: isOrgScoped ? form.orgId : '',
      applicability: form.applicability,
      validFrom: dateToEpoch(form.validFrom),
      validUntil: dateToEpoch(form.validUntil),
      maxTotalUses: isOrgScoped ? 1 : (maxUses === '' ? null : Number(maxUses)),
      active: form.active ? 1 : 0,
      grantDurationDays: form.customDuration ? grantToDays(form.grantValue, form.grantUnit) : null,
    };
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await updateActivationCode(code.activationCodeId, payload);
      } else {
        await createActivationCode(payload);
      }
      onSaved?.();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Scrim onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <Modal style={{ maxWidth: 520 }}>
        <div className="modal__head">
          <div>
            <h3>{isEdit ? 'Edit activation code' : 'New activation code'}</h3>
            <p>
              {isEdit
                ? 'Update the plan this code grants and its validity.'
                : 'Create a code an instructor can redeem to unlock a plan — no card required.'}
            </p>
          </div>
          <button className="x-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal__body">
          {error && (
            <div style={{ color: 'crimson', fontSize: 13, marginBottom: 14 }}>{error}</div>
          )}

          <Field>
            <label>Code type</label>
            <Select value={form.scope} onChange={(e) => set('scope', e.target.value)}>
              <option value="global">Global — anyone can redeem</option>
              <option value="org">Organization-specific — one-time, tied to an org</option>
            </Select>
          </Field>

          {isOrgScoped && (
            <Field>
              <label>Organization *</label>
              <Select value={form.orgId} onChange={(e) => set('orgId', e.target.value)} disabled={orgsLoading}>
                <option value="">
                  {orgsLoading
                    ? 'Loading organizations…'
                    : orgsError
                      ? 'Could not load organizations'
                      : orgs.length === 0
                        ? 'No organizations found'
                        : 'Select an organization…'}
                </option>
                {orgs.map((o) => (
                  <option key={o.orgId} value={o.orgId}>{(o.orgName || 'Unnamed')} · {o.orgId}</option>
                ))}
              </Select>
              {orgsError ? (
                <Hint style={{ color: 'crimson' }}>{orgsError}</Hint>
              ) : (
                <Hint>
                  Redeemable once, by a single user — max uses is locked to 1.
                  {orgs.length ? ` · ${orgs.length} organizations` : ''}
                </Hint>
              )}
            </Field>
          )}

          <Field>
            <label>Code *</label>
            <CodeRow>
              <Input
                value={form.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                placeholder="e.g. SCHOOL2026"
                style={{ textTransform: 'uppercase' }}
              />
              <Btn
                type="button"
                $variant="ghost"
                onClick={handleGenerate}
                disabled={generating}
                title="Generate a random code"
              >
                {generating ? <CircularProgress size={13} color="inherit" /> : 'Generate'}
              </Btn>
            </CodeRow>
          </Field>

          <FormGrid>
            <Field>
              <label>Plan</label>
              <Select value={form.planId} onChange={(e) => set('planId', e.target.value)}>
                {TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
            <Field>
              <label>Billing cycle</label>
              <Select value={form.applicability} onChange={(e) => set('applicability', e.target.value)}>
                {APPLICABILITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
          </FormGrid>

          <FormGrid>
            <Field>
              <label>Code valid from <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span></label>
              <Input
                type="date"
                value={form.validFrom}
                onChange={(e) => set('validFrom', e.target.value)}
              />
            </Field>
            <Field>
              <label>Code valid until <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span></label>
              <Input
                type="date"
                value={form.validUntil}
                onChange={(e) => set('validUntil', e.target.value)}
              />
            </Field>
          </FormGrid>
          <Hint style={{ marginTop: -6, marginBottom: 14 }}>
            The window during which this code can be redeemed — not the plan length.
          </Hint>

          <Field>
            <Toggle
              checked={form.customDuration}
              onChange={(v) => set('customDuration', v)}
              label="Set a custom plan duration"
            />
            {form.customDuration ? (
              <>
                <GrantRow style={{ marginTop: 10 }}>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={form.grantValue}
                    onChange={(e) => set('grantValue', e.target.value)}
                    placeholder="e.g. 2.5"
                  />
                  <Select value={form.grantUnit} onChange={(e) => set('grantUnit', e.target.value)}>
                    <option value="months">Months</option>
                    <option value="days">Days</option>
                  </Select>
                </GrantRow>
                <Hint>The plan expires this long after the user activates (e.g. 2.5 months).</Hint>
              </>
            ) : (
              <Hint>Off — the plan runs the standard {form.applicability === 'yearly' ? 'yearly (365 days)' : 'monthly (30 days)'} term.</Hint>
            )}
          </Field>

          <FormGrid style={{ marginBottom: 0 }}>
            <Field style={{ marginBottom: 0 }}>
              <label>Max total uses</label>
              {isOrgScoped ? (
                <Input value="1 — one-time" disabled readOnly />
              ) : (
                <Input
                  type="number"
                  min="0"
                  value={form.maxTotalUses}
                  onChange={(e) => set('maxTotalUses', e.target.value)}
                  placeholder="Unlimited"
                />
              )}
            </Field>
            <Field style={{ marginBottom: 0 }}>
              <label>Status</label>
              <Select
                value={String(form.active)}
                onChange={(e) => set('active', Number(e.target.value))}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Select>
            </Field>
          </FormGrid>
        </div>

        <div className="modal__foot">
          <Btn $variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? 'Save changes' : 'Create code')}
          </Btn>
        </div>
      </Modal>
    </Scrim>
  );
}

const CodeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  > input { flex: 1; }
  > button { flex: none; white-space: nowrap; }
`;

const GrantRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  > input { flex: 1; }
  > select { flex: none; width: 120px; }
`;

const Hint = styled.small`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.textMuted};
`;
